import { Fragment, useEffect, useRef, useState } from 'react';
import { type UseQueryResult } from '@tanstack/react-query';
import {
  AlertTriangle,
  Copy,
  CornerDownLeft,
  PanelLeft,
  PanelRight,
  Pencil,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { formatPhone } from '@/lib/format-phone';
import { useComposerBridge } from '@/features/chat/composer-bridge';
import {
  composeAccountInfo,
  GAME_SECTION_RANGE,
  MEMBER_INDEX,
} from '@/features/chat/profile-templates';
import {
  useCustomerProfile,
  type CustomerProfile,
} from '@/features/chat/useCustomerProfile';
import {
  useGameAccounts,
  useRegisterMember,
  useUpdateGameAccountField,
  type GameAccounts,
} from '@/features/chat/useGameAccounts';
import { usePrefsStore } from '@/store/prefs';

/**
 * Customer-profile sidebar — the 4th column (design §3), rendered as a flex
 * sibling of the chat column in BOTH chat views (`profileOpen` in the prefs
 * store): single conversation (see `ChatPanel`) and merged thread (see
 * `ThreadPanel`, which keys it on the currently chosen send-target member).
 *
 * Sections: header (title + side-toggle + close) · 档案卡 (MEMBER identity /
 * name / phone / WhatsApp pill — read-only; the MEMBER value comes from the
 * Google Sheets registry mirror, em-dash when absent, an amber 重复注册 badge
 * when the phone is registered twice, a sky 新顾客 badge when the number is
 * not in the registry at all) · 游戏账号 (one EDITABLE row per
 * registry column — cell edits write straight back to the spreadsheet, see
 * `useUpdateGameAccountField`; replaced wholesale by a cleanup warning on a
 * duplicate phone, or by the 注册会员 panel for a new customer, see
 * `useRegisterMember`) · 备注 (the sheet's O column, edited in a big
 * blur-saving box through the SAME field PATCH as the rows — see
 * `SheetNoteField`; the Hub-DB contact note is retired from the console).
 * Customer tags and the 转交/关闭会话 footer actions are intentionally
 * OMITTED (separate features).
 *
 * 插入 (spec F3 / T3-F): every registry data row with a value, and the
 * MEMBER identity, has a 「插入」 button, and the 游戏账号 header offers
 * 「插入账号资料」 (all non-empty game slots at once). They put the value
 * into the composer draft of the SAME conversation through the composer
 * bridge (`composer-bridge.ts`) — INSERT ONLY: the sidebar never sends a
 * message, the agent reviews the draft and sends it. The internal note
 * boxes (备注 / Remark) and the hidden K column are never insertable.
 *
 * The sidebar is a `.neu-panel-mid` surface: same background tone and re-toned
 * twin-shadow vars as the conversation column, matching the design's
 * `--b2` (中栏 / 资料栏背景).
 */

interface ProfileSidebarProps {
  conversationId: string;
}

export function ProfileSidebar({ conversationId }: ProfileSidebarProps): React.JSX.Element {
  const profileSide = usePrefsStore((s) => s.profileSide);
  const setProfileOpen = usePrefsStore((s) => s.setProfileOpen);
  const setProfileSide = usePrefsStore((s) => s.setProfileSide);

  const query = useCustomerProfile(conversationId);

  return (
    <aside
      aria-label="客户资料"
      className="neu-panel-mid flex h-full min-h-0 w-[300px] shrink-0 flex-col overflow-y-auto px-4 pb-4 pt-5"
    >
      {/* Header — title + side-toggle (⇤/⇥) + close ✕ (design §3 头部). */}
      <header className="flex shrink-0 items-center gap-2 pb-4">
        <h2 className="flex-1 truncate text-[15px] font-extrabold">客户资料</h2>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={profileSide === 'right' ? '移到左侧' : '移到右侧'}
          className="size-8 shrink-0 rounded-[11px] text-muted-foreground shadow-neu-raised-sm hover:shadow-neu-raised active:shadow-neu-inset-press"
          onClick={() => setProfileSide(profileSide === 'right' ? 'left' : 'right')}
        >
          {profileSide === 'right' ? (
            <PanelLeft className="size-4" />
          ) : (
            <PanelRight className="size-4" />
          )}
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="关闭客户资料"
          className="size-8 shrink-0 rounded-[11px] text-muted-foreground shadow-neu-raised-sm hover:shadow-neu-raised active:shadow-neu-inset-press"
          onClick={() => setProfileOpen(false)}
        >
          <X className="size-4" />
        </Button>
      </header>

      {query.isLoading ? (
        <ProfileSkeleton />
      ) : query.isError || !query.data ? (
        <ProfileError />
      ) : (
        <ProfileBody conversationId={conversationId} profile={query.data} />
      )}
    </aside>
  );
}

// --- Body ------------------------------------------------------------------

interface ProfileBodyProps {
  conversationId: string;
  profile: CustomerProfile;
}

function ProfileBody({ conversationId, profile }: ProfileBodyProps): React.JSX.Element {
  // ONE game-accounts subscription for the whole body: the identity card
  // reads the MEMBER value out of it and the 游戏账号 section renders the
  // rest (both key on the same cache slot, so this is a single fetch).
  const gameAccountsQuery = useGameAccounts(conversationId);

  // Display name falls back to the formatted phone, then the raw peer id, so
  // an unnamed contact still shows a stable identifier.
  const nameLine =
    profile.display_name?.trim() ||
    formatPhone(profile.phone_e164) ||
    profile.peer_id;

  // A duplicated phone has NO mirror row by rule, so there is no single
  // MEMBER to show — the identity slot becomes a warning badge instead.
  const isDuplicate = gameAccountsQuery.data?.duplicate === true;

  // A customer the registry has never heard of, who nonetheless HAS a number
  // we could register. `found=false` alone is not enough: it also covers a
  // group chat (no phone — nothing to register) and the duplicate data error,
  // and an em-dash would read as "still loading" for all three alike.
  const isNewCustomer =
    gameAccountsQuery.data?.found === false &&
    !isDuplicate &&
    gameAccountsQuery.data.phone_e164 !== null;

  // The registry MEMBER value is the customer's PRIMARY identity line. The
  // MEMBER column is located by POSITION (`columns[MEMBER_INDEX]` names the
  // header, the value is looked up under that name in `fields`); a payload
  // without `columns` (no successful sync yet) falls back to the literal
  // MEMBER header name. An em-dash covers every "absent" flavor alike: query
  // still loading / failed, found=false, or a MEMBER cell that is simply
  // empty.
  const registryColumns = gameAccountsQuery.data?.columns ?? null;
  const memberColumn =
    registryColumns === null
      ? MEMBER_FALLBACK_COLUMN
      : registryColumns.length > MEMBER_INDEX
        ? registryColumns[MEMBER_INDEX]
        : null;
  const memberValue =
    memberColumn === null
      ? ''
      : gameAccountsQuery.data?.fields.find(
          ([name]) => name.toUpperCase() === memberColumn.toUpperCase(),
        )?.[1] ?? '';
  // The MEMBER identity is insertable like any registry data row — with a
  // value, and only when the positions are known (same rule as the rows, see
  // `GameAccountsBody`). The duplicate / new-customer / group states never
  // qualify: they carry no column list and no MEMBER value.
  const memberInsertColumn =
    registryColumns !== null && memberValue !== '' ? memberColumn : null;

  return (
    <div className="flex min-h-0 flex-col gap-4">
      {/* 档案卡 — raised read-only card: MEMBER identity + name + phone +
          WhatsApp pill (no avatar; the registry MEMBER is the identity). */}
      <div className="flex flex-col items-center gap-1.5 rounded-[18px] px-4 py-5 shadow-neu-raised">
        {isDuplicate ? (
          <span
            role="status"
            data-testid="game-accounts-duplicate-badge"
            className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-amber-500/20 px-3 py-1 text-[14px] font-extrabold tracking-wide text-amber-700 dark:bg-amber-500/25 dark:text-amber-300"
          >
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
            重复注册
          </span>
        ) : isNewCustomer ? (
          <span
            role="status"
            data-testid="game-accounts-new-badge"
            className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-sky-500/15 px-3 py-1 text-[14px] font-extrabold tracking-wide text-sky-700 dark:bg-sky-500/20 dark:text-sky-300"
          >
            <UserPlus className="size-3.5 shrink-0" aria-hidden="true" />
            新顾客
          </span>
        ) : (
          <div className="flex max-w-full items-center justify-center gap-1">
            <p
              title={memberValue || undefined}
              className="min-w-0 truncate text-center text-[20px] font-extrabold tracking-wide"
            >
              {memberValue || '—'}
            </p>
            {memberInsertColumn !== null && (
              <InsertButton
                conversationId={conversationId}
                column={memberInsertColumn}
                value={memberValue}
              />
            )}
          </div>
        )}
        <p className="max-w-full truncate text-center text-[13px] font-bold text-muted-foreground">
          {nameLine}
        </p>
        {profile.phone_e164 && (
          <p className="text-[12.5px] font-bold tabular-nums text-muted-foreground">
            {formatPhone(profile.phone_e164)}
          </p>
        )}
        <span className="neu-gradient mt-1 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold text-white">
          WhatsApp
        </span>
      </div>

      {/* The registry sections — 备注 included: it IS a registry column now
          (the O slot), so `GameAccountsBody` owns its placement in the
          running order. */}
      <GameAccountsSection conversationId={conversationId} query={gameAccountsQuery} />
    </div>
  );
}

// --- 游戏账号 (game-accounts registry) --------------------------------------

/**
 * POSITION-BASED sectioning. `columns[i]` is sheet column B+i: the Hub serves
 * the tab's header row minus column A (PHONE) — verbatim spelling, sheet
 * order (`hub/app/sheets/sync.py`, `tab_headers[tab] = header[1:]`). Sections
 * bind to POSITIONS, never to header names: the operators rename headers
 * freely (the BANK/EWALLET → BANK rename broke name matching twice), so the
 * layout below is the agreed contract with the sheet owner —
 *
 *   A = PHONE (dropped server-side) · B..I games · J MEMBER (identity card)
 *   · K operators' sheet-side input area (hidden) · L..N bank · O 备注
 *   (big-box editor) · P..Q refer · R onward Other (S = Remark, big-box).
 *
 * The header text stays verbatim as DATA — row label text, aria-labels and
 * the PATCH `column` all carry the sheet's own spelling; only the position
 * decides the section. (Visually the row-label span applies a CSS
 * `uppercase` transform — a style choice, not a data transform.)
 *
 * THE OPEN-ENDED GUARANTEE HOLDS: every index from `OTHER_START_INDEX` on
 * that this table does not claim falls through to Other and still renders,
 * in sheet order — a column appended to the sheet can never silently
 * disappear from the console; the worst case is that it shows up in the
 * catch-all group until it is given a slot here.
 *
 * Two of the positions live in `profile-templates.ts` because the insert
 * templates read them too: the 游戏账号 range (B..I, `GAME_SECTION_RANGE`)
 * and the MEMBER slot (J, `MEMBER_INDEX`). A column inserted or deleted in
 * the sheet means updating BOTH files.
 */

/** The 游戏账号 section's title — also the block that carries 「插入账号资料」. */
const GAME_SECTION_TITLE = '游戏账号';

const COLUMN_SECTIONS: ReadonlyArray<{ title: string; start: number; end: number }> = [
  { title: GAME_SECTION_TITLE, ...GAME_SECTION_RANGE }, // sheet B..I
  { title: 'Bank info', start: 10, end: 12 }, // sheet L..N
  { title: 'Refer', start: 14, end: 15 }, // sheet P..Q
];

/** Identity lookup for a payload WITHOUT `columns` (no successful sync yet):
 *  fall back to the agreed header name, compared case-insensitively. */
const MEMBER_FALLBACK_COLUMN = 'MEMBER';

/** `columns[]` indexes that never render at all: K (index 9) is the
 *  operators' sheet-side input area — not customer data. */
const HIDDEN_INDEXES: ReadonlySet<number> = new Set([9]);

/** `columns[]` index (sheet column O) — the 备注 big-box editor, slotted
 *  into the running order via `NOTE_BEFORE_SECTION` instead of rendering as
 *  a row. */
const NOTE_INDEX = 13;

/** `columns[]` index (sheet column S, "Remark") — rendered INSIDE Other at
 *  its sheet-order position, but as a big-box editor instead of a row. */
const REMARK_BOX_INDEX = 17;

/** First `columns[]` index of the open-ended Other tail (sheet column R). */
const OTHER_START_INDEX = 16;

/** Section title for every unclaimed index ≥ `OTHER_START_INDEX`. */
const OTHER_SECTION_TITLE = 'Other';

/**
 * 备注 renders immediately BEFORE this section, giving the running order
 * 游戏账号 · Bank info · 备注 · Refer · Other. A tab too narrow for Refer
 * columns has no such section, so the note falls back to just before Other,
 * and then to last — it can never disappear, whatever the sheet's width
 * turns out to be.
 */
const NOTE_BEFORE_SECTION = 'Refer';

/**
 * Per-column help, keyed by `columns[]` index (position, like everything
 * else — the header spelling is the operators' to change). Shown as the
 * editor's placeholder and as the row's tooltip — a format that is not
 * obvious from the column name has to be explained where it is typed, not
 * in a doc nobody opens.
 */
const COLUMN_HINTS: Readonly<Record<number, string>> = {
  // Sheet Q — REFER_TO.
  15:
    '这位顾客推荐加入的朋友 WhatsApp 号码，用逗号分隔，' +
    '例如 0123456111, 0168818745, 01152529878',
};

/** One renderable column of a section — everything position-derived is
 *  resolved here so the render loop never sees an index again. */
interface SectionColumn {
  /** The SHEET's header text, verbatim — the row-label text (CSS may re-case
   *  it visually) AND the PATCH column. */
  name: string;
  /** Position-keyed format help (see `COLUMN_HINTS`). */
  hint?: string;
  /** Render as a big note-style box (`SheetNoteField`) instead of a row. */
  box?: boolean;
}

/**
 * Split a tab's data columns into the display sections, by POSITION.
 *
 * Each configured section takes its index range (clamped to the tab's actual
 * width — a slot the tab is too narrow to have simply doesn't exist), so
 * every section's rows keep the spreadsheet's own ordering. The open-ended
 * Other tail collects every index from `OTHER_START_INDEX` on. Empty
 * sections are dropped, so a narrow tab simply has fewer headings.
 */
function groupColumns(columns: string[]): Array<{ title: string; columns: SectionColumn[] }> {
  const at = (index: number): SectionColumn => ({
    name: columns[index],
    hint: COLUMN_HINTS[index],
    box: index === REMARK_BOX_INDEX,
  });
  const groups = COLUMN_SECTIONS.map(({ title, start, end }) => {
    const present: SectionColumn[] = [];
    for (let i = start; i <= end && i < columns.length; i += 1) {
      if (!HIDDEN_INDEXES.has(i)) present.push(at(i));
    }
    return { title, columns: present };
  });
  const other: SectionColumn[] = [];
  for (let i = OTHER_START_INDEX; i < columns.length; i += 1) {
    if (!HIDDEN_INDEXES.has(i)) other.push(at(i));
  }
  if (other.length > 0) {
    groups.push({ title: OTHER_SECTION_TITLE, columns: other });
  }
  return groups.filter((group) => group.columns.length > 0);
}

/** Backend cap for one game-account cell (`MAX_GAME_ACCOUNT_VALUE_LENGTH`
 *  in `hub/app/api/schemas.py`) — mirrored on the inline editor input. */
const FIELD_MAX_LENGTH = 500;

/** One titled block of the sidebar — the shared heading + body wrapper.
 *  `action` (optional) sits at the right end of the heading line. */
function SidebarBlock({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  const heading = (
    <span
      data-testid="sidebar-block-title"
      className="text-[11px] font-extrabold text-muted-foreground"
    >
      {title}
    </span>
  );
  return (
    <div className="flex flex-col gap-1.5">
      {action ? (
        <div className="flex items-center justify-between gap-2">
          {heading}
          {action}
        </div>
      ) : (
        heading
      )}
      {children}
    </div>
  );
}

interface GameAccountsSectionProps {
  conversationId: string;
  query: UseQueryResult<GameAccounts, Error>;
}

/**
 * 游戏账号 section — the Google Sheets registry, cell-editable (see
 * `useGameAccounts` / `useUpdateGameAccountField`). Renders its OWN query
 * states inside the section so a sheet-mirror hiccup never blanks the rest
 * of the profile: 2 skeleton rows while loading, a muted error line on
 * failure, a muted "none" hint when there is no number to key on at all
 * (`found=false` with `phone_e164: null` — a group chat), the 新顾客
 * registration panel when the number simply is not in the registry
 * (`found=false` WITH a phone — the other normal `found=false` state), an
 * amber cleanup warning when the phone is registered twice
 * (`duplicate=true`), else ONE row per entry of the tab's `columns` list
 * (minus the positions with other homes — MEMBER in the identity card, the
 * K input area hidden, the O 备注 box in its own slot) INCLUDING columns
 * whose cell is empty for this customer (fillable 点击填写 rows), plus a
 * source/sync-age footer. The non-data states render NO 备注 box — there is
 * no sheet row to write it to.
 */
function GameAccountsSection({
  conversationId,
  query,
}: GameAccountsSectionProps): React.JSX.Element {
  if (query.isLoading) {
    return (
      <SidebarBlock title="游戏账号">
        <div className="flex flex-col gap-2.5">
          {[0, 1].map((i) => (
            <Skeleton
              key={i}
              data-testid="game-accounts-skeleton"
              className="h-10 rounded-[12px]"
            />
          ))}
        </div>
      </SidebarBlock>
    );
  }
  if (query.isError || !query.data) {
    return (
      <SidebarBlock title="游戏账号">
        <p className="px-1 py-1 text-xs font-semibold text-muted-foreground">
          无法加载游戏账号
        </p>
      </SidebarBlock>
    );
  }
  return <GameAccountsBody conversationId={conversationId} data={query.data} />;
}

interface GameAccountsBodyProps {
  conversationId: string;
  data: GameAccounts;
}

function GameAccountsBody({
  conversationId,
  data,
}: GameAccountsBodyProps): React.JSX.Element {
  // Duplicate FIRST: the contract pins `found=false` on a duplicate phone, so
  // the plain "none" hint would swallow the data error otherwise.
  if (data.duplicate) {
    return (
      <SidebarBlock title="游戏账号">
        <DuplicateWarning members={data.duplicate_members ?? []} />
        {/* The mirror's freshness stamp is still meaningful — the phone was
            excluded by the sync, not missed by it. */}
        <SyncFooter syncedAt={data.synced_at} />
      </SidebarBlock>
    );
  }

  if (!data.found) {
    // No phone (a group chat) — there is no number to register, so the plain
    // empty line stays. Everything else with a number is a NEW CUSTOMER.
    return (
      <SidebarBlock title="游戏账号">
        {data.phone_e164 === null ? (
          <p className="px-1 py-1 text-xs font-semibold text-muted-foreground">
            暂无游戏账号资料
          </p>
        ) : (
          <NewCustomerPanel conversationId={conversationId} />
        )}
      </SidebarBlock>
    );
  }

  // The FULL data-column list of the customer's source tab drives the rows,
  // sectioned by POSITION — a column with no value for this customer still
  // renders (fillable). The fields-derived fallback only covers a degenerate
  // found=true payload with columns=null (contract says that can't happen;
  // degrade, don't blank): with no positions there are no sections and no
  // big-box editors, so every field renders as a plain row under Other
  // (minus the MEMBER fallback header — that value lives in the identity
  // card).
  const columns = data.columns;
  const fallbackRows =
    columns !== null
      ? []
      : data.fields
          .filter(([name]) => name.toUpperCase() !== MEMBER_FALLBACK_COLUMN)
          .map(([name]): SectionColumn => ({ name }));
  const groups =
    columns !== null
      ? groupColumns(columns)
      : fallbackRows.length > 0
        ? [{ title: OTHER_SECTION_TITLE, columns: fallbackRows }]
        : [];
  // Keyed by UPPERCASED column name so a casing drift between the `columns`
  // list and the `fields` pairs (both echo the sheet header, but from
  // different sync moments) can never orphan a value.
  const valueByColumn = new Map(
    data.fields.map(([name, cell]) => [name.toUpperCase(), cell] as const),
  );
  const valueOf = (name: string): string => valueByColumn.get(name.toUpperCase()) ?? '';

  // 插入 needs POSITIONS: in the columns=null fallback the internal note
  // columns (O 备注 / S Remark) render as plain rows and cannot be told apart
  // from customer data, so nothing is insertable there. With positions,
  // every data row is (the note boxes are never rows).
  const insertable = columns !== null;
  // 「插入账号资料」 — every non-empty 游戏账号 slot at once. Past the early
  // returns above this is found && !duplicate; offered only when at least
  // one game slot holds a value.
  const accountInfo = columns !== null ? composeAccountInfo(columns, valueOf) : '';

  // The O-column 备注 box — only when the tab is wide enough to HAVE an O
  // column (and never in the columns=null fallback).
  const noteColumn =
    columns !== null && columns.length > NOTE_INDEX ? columns[NOTE_INDEX] : null;
  const note =
    noteColumn === null ? null : (
      <SheetNoteField
        conversationId={conversationId}
        column={noteColumn}
        value={valueOf(noteColumn)}
        label="备注"
        ariaLabel="备注"
        placeholder="输入备注…"
      />
    );

  if (groups.length === 0) {
    // A tab with no data columns at all — footer still applies. (A tab this
    // narrow cannot have an O column either, so no note box.)
    return (
      <SidebarBlock title="游戏账号">
        <p className="px-1 py-1 text-xs font-semibold text-muted-foreground">
          暂无游戏账号资料
        </p>
        <SyncFooter syncedAt={data.synced_at} />
      </SidebarBlock>
    );
  }

  // One titled block per group, with 备注 slotted into its own place in the
  // running order (see NOTE_BEFORE_SECTION). Falls back to just-before-Other
  // and then to last, so the note survives any column set. The freshness
  // footer hangs off the final REGISTRY block, so it still reads as belonging
  // to the registry rather than to the note.
  const indexOf = (title: string) => groups.findIndex((group) => group.title === title);
  const noteBefore = [indexOf(NOTE_BEFORE_SECTION), indexOf(OTHER_SECTION_TITLE)].find(
    (index) => index !== -1,
  ) ?? groups.length;
  return (
    <>
      {groups.map((group, groupIndex) => (
        <Fragment key={group.title}>
          {groupIndex === noteBefore && note}
          <SidebarBlock
            title={group.title}
            action={
              group.title === GAME_SECTION_TITLE && accountInfo !== '' ? (
                <InsertAccountInfoButton conversationId={conversationId} text={accountInfo} />
              ) : undefined
            }
          >
            <div className="flex flex-col gap-2.5">
              {group.columns.map((column, index) =>
                column.box ? (
                  // The S-column Remark box — same big editor as 备注, at its
                  // sheet-order position inside the section flow.
                  <SheetNoteField
                    key={`${index}-${column.name}`}
                    conversationId={conversationId}
                    column={column.name}
                    value={valueOf(column.name)}
                    label={column.name}
                    ariaLabel={column.name}
                    placeholder={`输入 ${column.name}…`}
                  />
                ) : (
                  <GameAccountRow
                    key={`${index}-${column.name}`}
                    conversationId={conversationId}
                    name={column.name}
                    hint={column.hint}
                    value={valueOf(column.name)}
                    insertable={insertable}
                  />
                ),
              )}
            </div>
            {groupIndex === groups.length - 1 && <SyncFooter syncedAt={data.synced_at} />}
          </SidebarBlock>
        </Fragment>
      ))}
      {noteBefore === groups.length && note}
    </>
  );
}

/** Past this age the footer turns amber: the agent is about to paste these
 *  values, and the mirror normally lags by at most one 5-minute sync. */
const SYNC_STALE_AFTER_MS = 30 * 60_000;

/**
 * 数据双向同步 Google Sheets (+ `同步于 x 分钟前` when `synced_at` is present),
 * amber once the mirror is more than 30 minutes old (a broken sync is
 * otherwise invisible here). One component so the normal and duplicate
 * branches cannot drift apart.
 */
function SyncFooter({ syncedAt }: { syncedAt: string | null }): React.JSX.Element {
  const age = syncedAt ? formatSyncAge(syncedAt) : '';
  const footer = age ? `数据双向同步 Google Sheets · ${age}` : '数据双向同步 Google Sheets';
  const stale = syncedAt ? isSyncStale(syncedAt) : false;
  return (
    <p
      data-stale={stale ? 'true' : undefined}
      title={stale ? '表格超过 30 分钟未同步，使用前请核对' : undefined}
      className={
        stale
          ? 'px-1 pt-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300'
          : 'px-1 pt-0.5 text-[10px] font-semibold text-muted-foreground/80'
      }
    >
      {footer}
    </p>
  );
}

/**
 * 「插入」 — hands one registry value to the composer bridge: it lands in
 * the composer draft of the SAME conversation, at the caret, for the agent
 * to review and send. Never sends anything itself (spec F3 / T3-F).
 */
function InsertButton({
  conversationId,
  column,
  value,
}: {
  conversationId: string;
  /** The sheet header, verbatim — names the button for screen readers. */
  column: string;
  /** Inserted verbatim (what copy would write). */
  value: string;
}): React.JSX.Element {
  const requestInsert = useComposerBridge((s) => s.requestInsert);
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      aria-label={`插入 ${column} 到输入框`}
      title="插入到输入框"
      className="size-6 shrink-0 rounded-[8px] text-muted-foreground hover:text-foreground"
      onClick={() => requestInsert(conversationId, value)}
    >
      <CornerDownLeft className="size-3" />
    </Button>
  );
}

/**
 * 「插入账号资料」 in the 游戏账号 header — inserts every non-empty game slot
 * at once, one `COLUMN: VALUE` line each (`composeAccountInfo`). Insert
 * only, like the per-row button; the agent adds anything else (a password,
 * a note) by typing on in the composer.
 */
function InsertAccountInfoButton({
  conversationId,
  text,
}: {
  conversationId: string;
  text: string;
}): React.JSX.Element {
  const requestInsert = useComposerBridge((s) => s.requestInsert);
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      title="把所有有值的游戏账号插入到输入框"
      className="-my-1 h-6 shrink-0 gap-1 rounded-[8px] px-2 text-[10.5px] font-bold text-muted-foreground hover:text-foreground"
      onClick={() => requestInsert(conversationId, text)}
    >
      <CornerDownLeft className="size-3" aria-hidden="true" />
      插入账号资料
    </Button>
  );
}

/**
 * 新顾客 panel — what the 游戏账号 rows are REPLACED by when the customer's
 * number is not in the registry at all (`found=false` with a phone).
 *
 * The registry pre-creates rows carrying only their MEMBER id, so registering
 * takes no form: the Hub picks the reserved row below the last registered
 * member and types this customer's number into it (see `useRegisterMember`).
 * One button, therefore — and it is disabled while the write is in flight so
 * a double click cannot burn two member ids on one customer.
 *
 * On success the mutation reconciles the cache and this whole panel is
 * replaced by the member's rows; on failure the toast carries the server's
 * reason and the panel stays, so the agent can retry or go fix the sheet.
 */
function NewCustomerPanel({ conversationId }: { conversationId: string }): React.JSX.Element {
  const mutation = useRegisterMember();

  return (
    <div className="flex flex-col gap-2.5 rounded-[12px] px-3 py-3 shadow-neu-inset-sm">
      <p className="text-[12px] font-bold text-muted-foreground">
        该号码不在会员名册中
      </p>
      <Button
        type="button"
        data-testid="register-member"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate({ conversationId })}
        className="neu-gradient h-9 w-full rounded-[10px] text-[13px] font-extrabold text-white"
      >
        {mutation.isPending ? (
          '注册中…'
        ) : (
          <>
            <UserPlus className="size-4 shrink-0" aria-hidden="true" />
            注册会员
          </>
        )}
      </Button>
      <p className="text-[10.5px] font-semibold text-muted-foreground/80">
        将写入表格中下一个空缺的会员号
      </p>
    </div>
  );
}

/**
 * Duplicate-phone warning — what the 游戏账号 rows are REPLACED by when the
 * contact's number appears more than once in the registry's PHONE column.
 *
 * Operator rule: a duplicate is a data error, so the console deliberately
 * shows NO accounts (the sync excludes such phones from the mirror entirely —
 * there is no "first row wins" to fall back on). The agent gets the MEMBER
 * value of every offending row so they can go find and merge them in the
 * sheet; `members` may legitimately be EMPTY when none of the duplicate rows
 * filled a MEMBER cell. No rows, no editors, no copy buttons — nothing here
 * is safe to write back, since no unique row can be located (the field PATCH
 * 409s for the same reason).
 */
function DuplicateWarning({ members }: { members: string[] }): React.JSX.Element {
  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-[12px] bg-amber-500/15 px-3 py-3 text-amber-700 shadow-neu-inset-sm dark:bg-amber-500/20 dark:text-amber-300"
    >
      <p className="flex items-center gap-1.5 text-[12px] font-extrabold">
        <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
        该号码在表格中重复注册
      </p>
      {members.length === 0 ? (
        <p className="text-[12px] font-bold">未登记 MEMBER</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {members.map((member, index) => (
            <span
              key={`${index}-${member}`}
              data-testid="duplicate-member"
              className="max-w-full truncate rounded-[8px] bg-amber-500/20 px-2 py-1 font-mono text-[12px] font-bold tabular-nums dark:bg-amber-500/25"
            >
              {member}
            </span>
          ))}
        </div>
      )}
      <p className="text-[10.5px] font-semibold text-amber-700/75 dark:text-amber-300/75">
        请先在表格中清理重复行
      </p>
    </div>
  );
}

interface GameAccountRowProps {
  conversationId: string;
  /** The SHEET's column name. It is BOTH what the row shows and what the
   *  PATCH writes — the operators renamed the headers to read well, so the
   *  sheet is the single source of the display name and no alias table can
   *  drift away from it. */
  name: string;
  /** Format help for columns whose shape is not obvious — the editor's
   *  placeholder and the row's tooltip. */
  hint?: string;
  value: string;
  /** Offer 「插入」 for a non-empty value. True for every data row whenever
   *  the registry positions are known (see `GameAccountsBody`). */
  insertable: boolean;
}

/**
 * One EDITABLE registry row: column label (muted) · value (mono/tabular,
 * truncated — `title` carries the full value; empty
 * cells show a muted 点击填写 placeholder) · 插入 (insertable rows with a
 * non-empty value; puts the VALUE into the composer draft through the
 * composer bridge — never sends) · copy button (non-empty values
 * only; writes the VALUE, toasts 已复制 only after the clipboard write
 * succeeds, 复制失败 otherwise) · pencil (aria-label `编辑 {column}`).
 *
 * The label, the accessible name and the PATCH's `column` are all the SHEET's
 * header — the operators renamed the columns to read well, so there is no
 * alias table that could drift away from what we write back.
 *
 * Clicking the value/placeholder or the pencil swaps in an inline input:
 * Enter or blur saves through `useUpdateGameAccountField` (a save to the
 * CURRENT value is a no-op — no request), Escape cancels with no request.
 * While the PATCH is in flight the row shows a subtle 保存中… state AND both
 * editor entry points are dead — the value button is disabled and
 * `openEditor`/`save` are gated on the row's pending flag — so the editor
 * cannot reopen with a stale draft and blur-save a second, out-of-order
 * PATCH. On success the mutation reconciles the cache (the row re-renders
 * with the saved value), on failure the cache was never touched so the row
 * simply falls back to the previous value (the mutation hook toasts the
 * server detail, or 保存失败). 插入 and copy are hidden alike while saving.
 */
function GameAccountRow({
  conversationId,
  name,
  hint,
  value,
  insertable,
}: GameAccountRowProps): React.JSX.Element {
  const mutation = useUpdateGameAccountField();
  // Per-row in-flight flag (each row owns its own mutation instance): gates
  // BOTH editor entry points and the save itself while a PATCH is running.
  const pending = mutation.isPending;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // A cell that holds newlines (the operators paste blocks). It must never
  // be truncated to a single line — that hides the only complete copy of the
  // data — and it must be EDITED in a textarea: a single-line input silently
  // flattens newlines on save. A filled block scrolls inside a fixed 3-line
  // slot instead of growing the sidebar without bound.
  const multiline = value.includes('\n');
  // Enter/Escape both close the editor while the input still has focus — the
  // ref makes the trailing blur event a no-op instead of a second save.
  const skipBlurRef = useRef(false);

  function openEditor(): void {
    // Never reopen mid-save: the draft would seed from the still-old value
    // and its blur-save would race the in-flight PATCH.
    if (pending) return;
    skipBlurRef.current = false;
    setDraft(value);
    setEditing(true);
  }

  // Operator requirement: every registry identifier is typed UPPERCASE (the
  // sheet is kept uppercase by hand); Chinese text and digits pass through
  // `toUpperCase` unchanged. The two note-style boxes (`SheetNoteField`) are
  // exempt by construction — they never render this row.
  //
  // The transform is applied to the DOM *before* React's controlled commit:
  // if React's commit assigned a value that differs from what the DOM holds,
  // the browser would collapse the caret to the END of the field on every
  // lowercase keystroke, garbling mid-string edits. Writing the uppercased
  // string into the element (and restoring the selection, which the
  // assignment resets) keeps DOM === committed value, so the caret stays
  // put. During an IME composition (Chinese pinyin) the field briefly holds
  // lowercase latin composition text — rewriting it would break the
  // composition buffer, so the transform waits for the composed text to
  // commit; `save()` uppercases once more as the final guarantee.
  function applyUppercase(el: HTMLInputElement | HTMLTextAreaElement): void {
    const upper = el.value.toUpperCase();
    if (upper !== el.value) {
      const { selectionStart, selectionEnd } = el;
      el.value = upper;
      el.setSelectionRange(selectionStart, selectionEnd);
    }
    setDraft(upper);
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void {
    if ((e.nativeEvent as InputEvent).isComposing) {
      setDraft(e.target.value);
      return;
    }
    applyUppercase(e.target);
  }

  // Chromium fires the composed text's final `input` event still flagged
  // `isComposing`, so the commit never reaches `handleChange`'s uppercase
  // path — catch it here instead.
  function handleCompositionEnd(
    e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void {
    applyUppercase(e.currentTarget);
  }

  async function save(): Promise<void> {
    setEditing(false);
    // A save for this row is already in flight — never stack a second PATCH
    // (last-write-wins upstream would make their landing order a coin toss).
    if (mutation.isPending) return;
    // `toUpperCase` again at the commit point: an IME composition can leave
    // lowercase latin in the draft (see `handleChange`), and the sheet rule
    // is enforced on what is SAVED, not merely on what was echoed.
    const next = draft.trim().toUpperCase();
    // No-op when nothing changed (avoids a needless PATCH on every blur).
    if (next === value) return;
    try {
      await mutation.mutateAsync({ conversationId, column: name, value: next });
    } catch {
      // The mutation hook already toasted 保存失败; the cache was never
      // touched, so this row keeps rendering the previous value.
    }
  }

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void {
    // In a MULTI-LINE cell Enter has to stay a newline — hijacking it to save
    // would make the value unwritable without losing its shape. Blur saves.
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      skipBlurRef.current = true;
      void save();
    } else if (e.key === 'Escape') {
      // Cancel: close the editor, drop the draft, send nothing. Handled
      // locally — stop the event from reaching ChatPanelInner's document-
      // level Escape listener, which would otherwise close the whole
      // conversation on the same keystroke (same idiom as the Composer).
      e.stopPropagation();
      skipBlurRef.current = true;
      setEditing(false);
    }
  }

  function handleBlur(): void {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    void save();
  }

  async function handleCopy(): Promise<void> {
    try {
      // `navigator.clipboard` is undefined outside secure contexts — surface
      // that as a copy failure instead of throwing.
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(value);
      toast('已复制');
    } catch {
      toast('复制失败');
    }
  }

  return (
    <div
      className={`flex min-h-9 gap-2 rounded-[12px] py-1.5 pl-3.5 pr-1.5 shadow-neu-inset-sm ${
        multiline ? 'items-start' : 'items-center'
      }`}
    >
      <span
        className={`shrink-0 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground/80 ${
          multiline ? 'pt-1' : ''
        }`}
      >
        {name}
      </span>
      {editing ? (
        multiline ? (
          <Textarea
            autoFocus
            aria-label={`${name} 值`}
            placeholder={hint}
            maxLength={FIELD_MAX_LENGTH}
            rows={3}
            value={draft}
            onChange={handleChange}
            onCompositionEnd={handleCompositionEnd}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="min-w-0 flex-1 resize-none rounded-[8px] px-2 py-1 font-mono text-[12px] font-bold shadow-none"
          />
        ) : (
          <Input
            autoFocus
            aria-label={`${name} 值`}
            placeholder={hint}
            maxLength={FIELD_MAX_LENGTH}
            value={draft}
            onChange={handleChange}
            onCompositionEnd={handleCompositionEnd}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="h-7 min-w-0 flex-1 rounded-[8px] px-2 text-right font-mono text-[12px] font-bold tabular-nums shadow-none"
          />
        )
      ) : (
        <>
          {multiline ? (
            // A filled block: scrollable, NOT click-to-edit — a click would
            // open the editor the moment the agent tried to scroll it. The
            // pencil is the way in. Fixed height (not max-h) so a filled
            // block always occupies the full 3-line slot.
            <div
              title={hint}
              data-testid="game-account-block"
              className="h-[4.5rem] min-w-0 flex-1 overflow-y-auto whitespace-pre-line break-words text-left font-mono text-[12px] font-bold leading-snug text-foreground"
            >
              {value}
            </div>
          ) : (
            <button
              type="button"
              disabled={pending}
              title={hint ?? value ?? undefined}
              onClick={openEditor}
              className={
                value
                  ? 'min-w-0 flex-1 truncate text-right font-mono text-[12px] font-bold tabular-nums text-foreground'
                  : 'min-w-0 flex-1 truncate text-right text-[11px] font-semibold text-muted-foreground/70'
              }
            >
              {value || '点击填写'}
            </button>
          )}
          {pending ? (
            <span className="shrink-0 px-1 text-[10px] font-semibold text-muted-foreground">
              保存中…
            </span>
          ) : (
            <>
              {insertable && value && (
                <InsertButton conversationId={conversationId} column={name} value={value} />
              )}
              {value && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`复制 ${name}`}
                  className="size-6 shrink-0 rounded-[8px] text-muted-foreground hover:text-foreground"
                  onClick={() => void handleCopy()}
                >
                  <Copy className="size-3" />
                </Button>
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label={`编辑 ${name}`}
                className="size-6 shrink-0 rounded-[8px] text-muted-foreground hover:text-foreground"
                onClick={openEditor}
              >
                <Pencil className="size-3" />
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}

// --- Sheet note field (big-box column editor) ------------------------------

interface SheetNoteFieldProps {
  conversationId: string;
  /** Sheet column the box writes — the header text verbatim (the PATCH's
   *  `column`), located by POSITION by the caller (O = 备注, S = Remark). */
  column: string;
  /** The column's current server value ('' when the cell is empty). */
  value: string;
  label: string;
  ariaLabel: string;
  placeholder: string;
}

/**
 * Big note-style editor for ONE free-text registry column (the O 备注 slot
 * and the S Remark slot). The textarea is a controlled `draft` seeded from
 * the server value; it saves on blur through the SAME
 * `useUpdateGameAccountField` PATCH as the rows (which reconciles the cache
 * and toasts on failure). NO uppercase transform here — these are the only
 * two free-prose fields, exempt from the rows' uppercase rule. The
 * reconciliation rules:
 *
 *   - Switching conversations hard-resets the draft to the new value.
 *   - As the query loads / the server value changes, the draft follows it
 *     ONLY when the agent has no unsaved local edits (tracked via
 *     `seededRef`), so a mid-typing cache update never clobbers the draft.
 *   - A failed save keeps the draft, so the agent can retry without
 *     re-typing.
 */
function SheetNoteField({
  conversationId,
  column,
  value,
  label,
  ariaLabel,
  placeholder,
}: SheetNoteFieldProps): React.JSX.Element {
  const mutation = useUpdateGameAccountField();

  const [draft, setDraft] = useState(value);
  // The last server value the draft was seeded from — used to tell "no local
  // edits" (draft still equals it) from "unsaved edits" (agent has typed).
  const seededRef = useRef(value);
  // The conversation the component is CURRENTLY showing — a blur-save that
  // resolves after a fast conversation switch must not write the previous
  // customer's text into this one's draft (the PATCH itself is keyed by the
  // captured id and stays correct either way).
  const liveConversationRef = useRef(conversationId);

  // Hard reset on conversation switch: whatever was typed for the previous
  // customer must not bleed into the next one.
  useEffect(() => {
    liveConversationRef.current = conversationId;
    seededRef.current = value;
    setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Follow the server value as it loads / changes, but never overwrite
  // unsaved local edits.
  useEffect(() => {
    if (value === seededRef.current) return;
    setDraft((cur) => (cur === seededRef.current ? value : cur));
    seededRef.current = value;
  }, [value]);

  async function save(): Promise<void> {
    const next = draft.trim();
    // No-op when nothing changed (avoids a needless PATCH on every blur).
    if (next === value) return;
    try {
      await mutation.mutateAsync({ conversationId, column, value: next });
    } catch {
      // The mutation hook already toasted the server detail / 保存失败 and
      // never touched the cache — keep the draft for a retry.
      return;
    }
    // Stale save: the agent already switched conversations while the PATCH
    // was in flight — the cache is reconciled under the captured id, but the
    // draft now belongs to another customer. Leave it alone.
    if (liveConversationRef.current !== conversationId) return;
    // The mutation wrote server truth into the cache; normalize the draft to
    // the saved (trimmed) value so a follow-up blur is a clean no-op.
    seededRef.current = next;
    setDraft(next);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Escape') {
      // Swallowed locally — the document-level ESC-to-close listener would
      // otherwise unmount the whole panel mid-typing and DROP the unsaved
      // draft (unmount fires no blur). Blur instead, which saves through the
      // one blur-save path; the NEXT press (focus now outside) may close.
      e.stopPropagation();
      e.currentTarget.blur();
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-extrabold text-muted-foreground">{label}</span>
        {mutation.isPending && (
          <span className="text-[10px] font-semibold text-muted-foreground">保存中…</span>
        )}
      </div>
      <Textarea
        aria-label={ariaLabel}
        rows={4}
        maxLength={FIELD_MAX_LENGTH}
        placeholder={placeholder}
        value={draft}
        disabled={mutation.isPending}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => void save()}
        className="resize-none rounded-[12px] text-[12px] font-semibold leading-[1.55]"
      />
    </div>
  );
}

// --- Loading / error -------------------------------------------------------

function ProfileSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-3 rounded-[18px] px-4 py-5 shadow-neu-raised">
        <Skeleton className="h-6 w-36" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="flex flex-col gap-2.5">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 rounded-[12px]" />
        ))}
      </div>
      <Skeleton className="h-24 rounded-[12px]" />
    </div>
  );
}

function ProfileError(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1 px-1 py-6 text-sm text-muted-foreground">
      <p className="font-medium text-foreground">无法加载客户资料</p>
      <p className="text-xs">请稍后重试。</p>
    </div>
  );
}

// --- Helpers ---------------------------------------------------------------

/**
 * ISO datetime -> relative sync age (`同步于 x 分钟前` / `小时前` / `天前`).
 * Tiny local helper, no date lib. Returns `''` on an unparseable value so the
 * footer degrades to the plain source line instead of showing garbage.
 */
function formatSyncAge(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60_000));
  if (minutes < 60) return `同步于 ${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `同步于 ${hours} 小时前`;
  return `同步于 ${Math.floor(hours / 24)} 天前`;
}

/** `true` when the ISO sync stamp is older than `SYNC_STALE_AFTER_MS`; an
 *  unparseable stamp is not flagged (its age line is dropped instead). */
function isSyncStale(iso: string): boolean {
  const then = Date.parse(iso);
  return !Number.isNaN(then) && Date.now() - then > SYNC_STALE_AFTER_MS;
}
